import React, { useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';
import { Alert, Box, Button, IconButton, Stack, Tooltip, useTheme } from '@mui/material';
import {
  CameraAltOutlined as CaptureIcon,
  CloseRounded as CloseIcon,
  FlipCameraAndroidOutlined as SwitchIcon,
} from '@mui/icons-material';

interface CameraCaptureProps {
  /** The captured frame, as base64 JPEG. */
  onCapture: (b64: string) => void;
  onClose: () => void;
  /** Which camera to open first: the back one for documents, the front one for faces. */
  facingMode: 'environment' | 'user';
  /** The resolution asked of the camera. */
  idealWidth: number;
}

const MAX_WIDTH = 2000;

/** The message key for a camera that would not open, from the browser's error name. */
const cameraErrorKey = (error: unknown): string => {
  switch ((error as { name?: string })?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'screens.testPage.cameraDenied';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'screens.testPage.cameraNotFound';
    case 'NotReadableError':
    case 'AbortError':
      return 'screens.testPage.cameraBusy';
    default:
      return 'screens.testPage.cameraFailed';
  }
};

/** A live camera view with capture, switch-camera (when there are several) and close. */
export const CameraCapture: React.FC<CameraCaptureProps> = ({ onCapture, onClose, facingMode, idealWidth }) => {
  const t = useTranslate();
  const theme = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mountedRef = useRef(true);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [activeDeviceId, setActiveDeviceId] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const stopStream = () => streamRef.current?.getTracks().forEach((track) => track.stop());

  const startCamera = (deviceId?: string) => {
    stopStream();
    setErrorKey(null);
    // Outside a secure context (or in a browser without it) there is no camera API at all.
    if (!navigator.mediaDevices?.getUserMedia) {
      setErrorKey('screens.testPage.cameraUnavailable');
      return;
    }
    const constraints: MediaStreamConstraints = {
      video: deviceId
        ? { deviceId: { exact: deviceId }, width: { ideal: idealWidth } }
        : { facingMode, width: { ideal: idealWidth } },
    };
    navigator.mediaDevices.getUserMedia(constraints)
      .then((s) => {
        // Closed before the browser answered: release the camera instead of leaving it on.
        if (!mountedRef.current) {
          s.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
        setActiveDeviceId(s.getVideoTracks()[0]?.getSettings().deviceId ?? null);
      })
      .catch((error) => {
        if (mountedRef.current) setErrorKey(cameraErrorKey(error));
      });
  };

  useEffect(() => {
    mountedRef.current = true;
    startCamera();
    navigator.mediaDevices?.enumerateDevices()
      .then((all) => setDevices(all.filter((d) => d.kind === 'videoinput')))
      .catch(() => {});
    return () => {
      mountedRef.current = false;
      stopStream();
    };
  }, []);

  const switchCamera = () => {
    if (devices.length < 2) return;
    const currentIdx = devices.findIndex((d) => d.deviceId === activeDeviceId);
    startCamera(devices[(currentIdx + 1) % devices.length].deviceId);
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    let w = video.videoWidth, h = video.videoHeight;
    if (w > MAX_WIDTH) { h = (MAX_WIDTH / w) * h; w = MAX_WIDTH; }
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d')!.drawImage(video, 0, 0, w, h);
    const b64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
    stopStream();
    onCapture(b64);
  };

  const switchLabel = t('screens.testPage.switchCamera');
  const closeLabel = t('screens.testPage.closeCamera');

  return (
    <Stack spacing={1}>
      {errorKey && (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={() => startCamera()}>{t('screens.testPage.retryCamera')}</Button>}
        >
          {t(errorKey)}
        </Alert>
      )}
      <Box
        sx={{
          display: errorKey ? 'none' : 'block',
          backgroundColor: theme.palette.common.black,
          borderRadius: theme.custom.radius.small,
          overflow: 'hidden',
        }}
      >
        <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', display: 'block' }} />
      </Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button variant="contained" startIcon={<CaptureIcon />} onClick={capture} disabled={!!errorKey}>
          {t('test.face_recognition.capture')}
        </Button>
        {devices.length > 1 && (
          <Tooltip title={switchLabel}>
            <IconButton aria-label={switchLabel} onClick={switchCamera}><SwitchIcon /></IconButton>
          </Tooltip>
        )}
        <Tooltip title={closeLabel}>
          <IconButton aria-label={closeLabel} onClick={onClose}><CloseIcon /></IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
